// Looking at a photograph without leaving the page.
//
// Every photo list in the app is a row of thumbnails, and until now clicking
// one navigated away to the image itself - fine for one photo, tedious for
// the eight a customer sent of a wardrobe. This opens them in place and lets
// the arrow keys walk the rest of the set, so a scheduler deciding on a job
// can flick through what was sent without losing their place in the list.
//
// Nothing has to be wired up per page. Any link marked rel="lightbox" opens
// in it, which is the markup the photo lists already use.
//
// The set the arrows walk is the links inside the nearest element carrying
// data-lightbox-group, falling back to every such link on the page. A page
// showing one job's photos needs no group and steps through all of them; a
// page showing several jobs at once - the opportunities board, the rejections
// list - marks each job's row so the arrows stay within one donation rather
// than wandering into somebody else's sideboard.
//
// A link can carry data-lightbox-caption to say what is being looked at; the
// caption sits under the counter.
//
// Along the bottom sits every photo in the set as a thumbnail, so getting to
// the twelfth is one click rather than eleven presses of an arrow. It matters
// most where the page is not showing the whole set: UI::PhotoGrid draws six
// tiles and a "+12", and the twelve it is counting are reachable only here.

const LINK = 'a[rel=lightbox]';
const GROUP = '[data-lightbox-group]';

// The open lightbox, or null. Only ever one.
let open = null;

const linksAround = (link) => {
  const scope = link.closest(GROUP) || document;
  return Array.from(scope.querySelectorAll(LINK));
};

const build = () => {
  const overlay = document.createElement('div');
  overlay.className = 'lightbox';
  overlay.setAttribute('role', 'dialog');
  overlay.setAttribute('aria-modal', 'true');
  // The picture and its two chevrons are one row, the strip is another, so the
  // sheet is a column of the two. Without the stage the chevrons would sit
  // beside the strip rather than beside the photo.
  overlay.innerHTML = `
    <button type="button" class="lightbox-close" aria-label="Close">&times;</button>
    <div class="lightbox-stage">
      <button type="button" class="lightbox-step lightbox-prev" aria-label="Previous photo">&lsaquo;</button>
      <figure class="lightbox-figure">
        <img class="lightbox-image" alt="">
        <figcaption class="lightbox-caption"></figcaption>
      </figure>
      <button type="button" class="lightbox-step lightbox-next" aria-label="Next photo">&rsaquo;</button>
    </div>
    <nav class="lightbox-thumbs" aria-label="All photos"></nav>
  `;
  return overlay;
};

// What to draw a thumbnail with. The link points at the full-size photo, and
// twenty of those would be an expensive way to fill a strip 64px tall, so the
// picture already on the page is preferred - it is in the cache, so the strip
// appears with the lightbox rather than a moment after it.
const thumbSource = (link) => {
  const inner = link.querySelector('img');
  return (inner && inner.getAttribute('src')) || link.href;
};

// Built once, when the lightbox opens: the set cannot change while it is up.
// Lazy images because a job can carry thirty photos and only the few either
// side of the current one are on screen.
const fillThumbs = () => {
  const { overlay, links } = open;
  if (links.length < 2) return;

  const strip = overlay.querySelector('.lightbox-thumbs');

  links.forEach((link, index) => {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'lightbox-thumb';
    // "+12" and a row of pictures say nothing out loud, so each one says where
    // in the set it is.
    button.setAttribute('aria-label', `Photo ${index + 1} of ${links.length}`);

    const image = document.createElement('img');
    image.src = thumbSource(link);
    image.alt = '';
    image.loading = 'lazy';

    button.appendChild(image);
    button.addEventListener('click', () => show(index));
    strip.appendChild(button);
  });
};

// Which thumbnail is the one being looked at, and keeping it in view: stepping
// with the arrow keys walks off the end of a strip of thirty otherwise.
const markThumb = () => {
  const { overlay, index } = open;

  overlay.querySelectorAll('.lightbox-thumb').forEach((thumb, at) => {
    const current = at === index;
    thumb.classList.toggle('lightbox-thumb--current', current);
    if (!current) {
      thumb.removeAttribute('aria-current');
      return;
    }
    thumb.setAttribute('aria-current', 'true');
    // 'nearest' both ways: the strip scrolls to bring it in, the page behind
    // the sheet is not asked to move at all.
    if (thumb.scrollIntoView) thumb.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  });
};

const show = (index) => {
  const { overlay, links } = open;
  open.index = (index + links.length) % links.length;

  const link = links[open.index];
  const image = overlay.querySelector('.lightbox-image');
  const caption = overlay.querySelector('.lightbox-caption');
  const inner = link.querySelector('img');

  image.src = link.href;
  image.alt = link.dataset.lightboxCaption || (inner && inner.alt) || '';

  const counter = links.length > 1 ? `${open.index + 1} of ${links.length}` : '';
  caption.textContent = [link.dataset.lightboxCaption, counter]
    .filter(Boolean)
    .join(' · ');

  // One photo is not a slideshow.
  overlay.classList.toggle('lightbox-single', links.length < 2);

  markThumb();

  // The next one along is what somebody is about to ask for.
  if (links.length > 1) {
    const ahead = links[(open.index + 1) % links.length];
    new Image().src = ahead.href;
  }
};

const close = () => {
  if (!open) return;

  open.overlay.remove();
  document.body.classList.remove('lightbox-open');
  const { opener } = open;
  open = null;
  // Back where they were, for anybody navigating by keyboard.
  if (opener) opener.focus();
};

const step = (by) => show(open.index + by);

const onKeydown = (event) => {
  if (!open) return;

  if (event.key === 'Escape') {
    close();
  } else if (event.key === 'ArrowRight') {
    step(1);
  } else if (event.key === 'ArrowLeft') {
    step(-1);
  } else {
    return;
  }

  event.preventDefault();
  // THE KEY IS SPENT. Other things on the page close on Escape too - the live
  // view's stop drawer is one, and the photographs are opened from inside it -
  // and every one of them is bound on the document, so one press would reach
  // them all and a reader asking for one step back would get three. The
  // lightbox is the top of that stack whenever it is up, so it consumes the
  // press rather than letting it through.
  //
  // IMMEDIATE, because the other handlers are on this same element: ordinary
  // stopPropagation would still let every listener registered on the document
  // run. See lightboxIsOpen below, which is the other half - this only protects
  // handlers registered AFTER this one, and that ordering is not something a
  // page should have to guarantee.
  event.stopImmediatePropagation();
};

const openFrom = (link) => {
  close();

  const overlay = build();
  open = { overlay, links: linksAround(link), index: 0, opener: link };
  const index = open.links.indexOf(link);
  // A link outside its own group would be a markup mistake; show it anyway.
  if (index === -1) open.links = [link];

  overlay.addEventListener('click', (event) => {
    // The backdrop is everything the picture and the controls are not.
    if (event.target.closest('.lightbox-figure, .lightbox-step, .lightbox-close, .lightbox-thumbs')) return;
    close();
  });
  overlay.querySelector('.lightbox-close').addEventListener('click', close);
  overlay.querySelector('.lightbox-prev').addEventListener('click', () => step(-1));
  overlay.querySelector('.lightbox-next').addEventListener('click', () => step(1));

  document.body.appendChild(overlay);
  document.body.classList.add('lightbox-open');
  fillThumbs();
  show(Math.max(index, 0));
  overlay.querySelector('.lightbox-close').focus();
};

// WHETHER A PHOTOGRAPH IS CURRENTLY UP, for anything else on the page that
// binds Escape.
//
// BELT AND BRACES WITH THE stopImmediatePropagation ABOVE, and both halves are
// needed because the two cover opposite orderings. A handler registered after
// this one never runs at all, because the press is consumed; a handler
// registered BEFORE it - this is mounted from core.js, so anything in the page's
// own head could be - runs first and has to decline on its own. Without one of
// them, opening a photograph from the live view's stop drawer and pressing
// Escape closes the photograph AND the drawer behind it, and a reader who asked
// for one step back is returned to the wall.
export const lightboxIsOpen = () => open !== null;

let mounted = false;

// One listener for the whole document, so photos that arrive with a Turbo
// frame work without being mounted.
export const mountLightbox = () => {
  if (mounted) return;
  mounted = true;

  document.addEventListener('click', (event) => {
    const link = event.target.closest(LINK);
    if (!link) return;
    // Let a modified click open the image in a tab, as any link would.
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;

    event.preventDefault();
    openFrom(link);
  });

  document.addEventListener('keydown', onKeydown);
  // A lightbox left open across a page change would float over the new page.
  document.addEventListener('turbo:before-render', close);
};
